import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SaveButton from '@/components/SaveButton';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { 
  Building, 
  MapPin, 
  Globe,
  Star,
  BadgeCheck,
  ArrowLeft,
  ExternalLink,
  Mail,
  Phone,
  MessageCircle,
  Users,
  Eye,
  Share2,
  Edit,
  Trash2
} from 'lucide-react';
import { API, useAuth } from '@/App';

const BusinessDetail = () => {
  const { businessId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState(0);
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    fetchBusiness();
  }, [businessId]);

  const fetchBusiness = async () => {
    try {
      const response = await axios.get(`${API}/businesses/${businessId}`);
      setBusiness(response.data);
    } catch (error) {
      toast.error('Business not found');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (reviewRating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setSubmittingReview(true);
    try {
      await axios.post(`${API}/businesses/${businessId}/reviews`, {
        rating: reviewRating,
        review_text: reviewText || null
      });
      toast.success('Review submitted!');
      setReviewText('');
      setReviewRating(0);
      fetchBusiness();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const renderStars = (rating, interactive = false, size = 'w-5 h-5') => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${size} cursor-${interactive ? 'pointer' : 'default'} ${
              star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
            }`}
            onClick={() => interactive && setReviewRating(star)}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!business) return null;

  const isOwner = user && (business.is_owner || business.owner_id === user.user_id || business.poster_user_id === user.user_id);
  const canEdit = business.can_edit || isOwner;

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this business listing? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/businesses/${businessId}`);
      toast.success('Business listing deleted');
      navigate('/businesses');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete business listing');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <Link to="/businesses" className="inline-flex items-center text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Directory
            </Link>
            {isOwner && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate(`/businesses/${businessId}/edit`)} data-testid="edit-business-btn">
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button variant="destructive" size="sm" onClick={handleDelete} data-testid="delete-business-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </div>
            )}
          </div>

          {/* Owner Actions Banner */}
          {canEdit && (
            <div className="bg-primary/5 border border-primary/20 rounded-xl px-4 py-3 mb-6 flex items-center justify-between">
              <span className="text-sm font-medium text-primary">This is your listing</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => navigate(`/businesses/${businessId}/edit`)} data-testid="edit-business-banner-btn">
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="destructive" onClick={handleDelete} data-testid="delete-business-banner-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </div>
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <div className="bg-white rounded-3xl overflow-hidden border-2 border-border mb-6">
              {/* Cover Image */}
              <div className="h-48 bg-gradient-to-br from-primary/20 to-accent/20 relative">
                {business.cover_image && (
                  <img src={business.cover_image} alt="" className="w-full h-full object-cover" />
                )}
              </div>

              <div className="p-8 relative">
                {/* Logo */}
                <div className="absolute -top-12 left-8">
                  <div className="w-24 h-24 bg-white rounded-2xl border-4 border-white shadow-lg flex items-center justify-center overflow-hidden">
                    {business.logo ? (
                      <img src={business.logo} alt={business.name} className="w-full h-full object-cover" />
                    ) : (
                      <Building className="w-12 h-12 text-muted-foreground" />
                    )}
                  </div>
                </div>

                <div className="pt-8">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h1 className="text-2xl md:text-3xl font-heading font-bold">{business.name}</h1>
                        {business.is_verified && (
                          <BadgeCheck className="w-6 h-6 text-blue-500" />
                        )}
                      </div>
                      <p className="text-lg text-muted-foreground">{business.category}</p>
                    </div>
                    <div className="flex gap-2">
                      {business.is_expat_owned && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-accent/10 text-accent font-medium">
                          <Globe className="w-4 h-4" />
                          Expat-Owned
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 mt-4">
                    <div className="flex items-center gap-2">
                      {renderStars(business.average_rating)}
                      <span className="font-bold">{business.average_rating.toFixed(1)}</span>
                      <span className="text-muted-foreground">({business.review_count} reviews)</span>
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Eye className="w-4 h-4" />
                      {business.views} views
                    </div>
                  </div>

                  <p className="mt-4 text-muted-foreground">{business.description}</p>

                  {/* Contact Buttons */}
                  <div className="flex flex-wrap gap-3 mt-6">
                    {business.website && (
                      <a href={business.website} target="_blank" rel="noopener noreferrer">
                        <Button className="rounded-full">
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Visit Website
                        </Button>
                      </a>
                    )}
                    {business.email && (
                      <a href={`mailto:${business.email}`}>
                        <Button variant="outline" className="rounded-full">
                          <Mail className="w-4 h-4 mr-2" />
                          Email
                        </Button>
                      </a>
                    )}
                    {business.phone && (
                      <a href={`tel:${business.phone}`}>
                        <Button variant="outline" className="rounded-full">
                          <Phone className="w-4 h-4 mr-2" />
                          Call
                        </Button>
                      </a>
                    )}
                    <SaveButton 
                      itemType="business" 
                      itemId={business.business_id}
                      className="rounded-full"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Details */}
              <div className="md:col-span-2 space-y-6">
                {/* Services */}
                {business.services_offered?.length > 0 && (
                  <div className="bg-white rounded-2xl p-6 border-2 border-border">
                    <h2 className="text-xl font-bold mb-4">Services Offered</h2>
                    <div className="flex flex-wrap gap-2">
                      {business.services_offered.map((service, index) => (
                        <span key={index} className="px-3 py-1 bg-muted rounded-full text-sm">
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reviews */}
                <div className="bg-white rounded-2xl p-6 border-2 border-border">
                  <h2 className="text-xl font-bold mb-4">Reviews</h2>
                  
                  {/* Write Review */}
                  {user && (
                    <div className="mb-6 pb-6 border-b border-border">
                      <p className="font-medium mb-2">Rate this business</p>
                      {renderStars(reviewRating, true, 'w-8 h-8')}
                      <Textarea
                        value={reviewText}
                        onChange={(e) => setReviewText(e.target.value)}
                        placeholder="Share your experience (optional)..."
                        className="mt-3"
                        rows={3}
                      />
                      <Button
                        onClick={handleSubmitReview}
                        disabled={submittingReview || reviewRating === 0}
                        className="mt-3 rounded-full"
                      >
                        {submittingReview ? 'Submitting...' : 'Submit Review'}
                      </Button>
                    </div>
                  )}

                  {/* Review List */}
                  {business.reviews?.length > 0 ? (
                    <div className="space-y-4">
                      {business.reviews.map((review) => (
                        <div key={review.review_id} className="pb-4 border-b border-border last:border-0">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                                <span className="text-sm font-bold text-primary">
                                  {review.reviewer_name.charAt(0)}
                                </span>
                              </div>
                              <span className="font-medium">{review.reviewer_name}</span>
                            </div>
                            {renderStars(review.rating, false, 'w-4 h-4')}
                          </div>
                          {review.review_text && (
                            <p className="text-muted-foreground">{review.review_text}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">No reviews yet. Be the first to review!</p>
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <div className="bg-white rounded-2xl p-6 border-2 border-border">
                  <h3 className="font-bold mb-4">Location</h3>
                  <div className="flex items-start gap-2 text-muted-foreground">
                    <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div>
                      {business.address && <p>{business.address}</p>}
                      <p>{business.city}, {business.country}</p>
                    </div>
                  </div>
                </div>

                {business.languages_spoken?.length > 0 && (
                  <div className="bg-white rounded-2xl p-6 border-2 border-border">
                    <h3 className="font-bold mb-4">Languages Spoken</h3>
                    <div className="flex flex-wrap gap-2">
                      {business.languages_spoken.map((lang, index) => (
                        <span key={index} className="px-3 py-1 bg-muted rounded-full text-sm">
                          {lang}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-white rounded-2xl p-6 border-2 border-border">
                  <h3 className="font-bold mb-4">Listed By</h3>
                  <p className="text-muted-foreground">{business.owner_name}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BusinessDetail;
